import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { errorToast, successToast } from '../../../Toast';
import loadAssetsClient from './loadAssetsClient';

const ssoApiClient = axios.create({
	baseURL: '/loadassets',
});

export interface TableResponse {
	locator: string;
	tableName: string;
	effectiveTimestamp: number;
	url: string;
	urlExpirationTimestamp: number;
}

interface TablesRequest {
	asOfTimestamp: number;
	tableName: string;
}

export interface AddTableRequest {
	asOfTimestamp: number;
	tableName: string;
	file: any;
}
interface SuccessfulTable {
	locator: string;
	tableName: string;
	effectiveTimestamp: number;
	url: string;
	urlExpirationTimestamp: number;
}

interface FailedTable {
	tableName: string;
	effectiveTimestamp: number;
	errorDetails: string;
}

interface AddTablesResponse {
	successfulTables: SuccessfulTable[];
	failedTables: FailedTable[];
}

const getTables = async (request: TablesRequest, authToken: string) => {
	let url = '/tables';

	const params = new URLSearchParams();

	if (request.asOfTimestamp) {
		params.append('asOfTimestamp', request.asOfTimestamp.toString());
	}
	if (request.tableName) {
		params.append('tableName', request.tableName);
	}

	if (Array.from(params).length > 0) {
		url += `?${params.toString()}`;
	}

	const res = await loadAssetsClient.get<TableResponse[]>(url, {
		headers: {
			Authorization: authToken,
		},
	});

	return res.data;
};

const getTablesSSO = async (request: TablesRequest, tenantHostname: string) => {
	let url = '/tables';

	const params = new URLSearchParams();

	if (request.asOfTimestamp) {
		params.append('asOfTimestamp', request.asOfTimestamp.toString());
	}
	if (request.tableName) {
		params.append('tableName', request.tableName);
	}

	params.append('ns_tenant_host', tenantHostname);

	if (Array.from(params).length > 0) {
		url += `?${params.toString()}`;
	}

	const res = await ssoApiClient.get<TableResponse[]>(url);

	return res.data;
};

const addTable = async (request: AddTableRequest, authToken: string) => {
	const formData = new FormData();
	formData.append('file', request.file);
	request.asOfTimestamp &&
		formData.append('effectiveTimestamp', request.asOfTimestamp.toString());
	request.tableName && formData.append('tableName', request.tableName);

	let url = '/tables';

	const response = await loadAssetsClient.put<AddTablesResponse>(
		url,
		formData,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);
	return response.data;
};

const addTableSSO = async (
	request: AddTableRequest,
	tenantHostname: string,
) => {
	const formData = new FormData();
	formData.append('file', request.file);
	request.asOfTimestamp &&
		formData.append('effectiveTimestamp', request.asOfTimestamp.toString());
	request.tableName && formData.append('tableName', request.tableName);

	let url = '/tables?ns_tenant_host=' + tenantHostname;

	const response = await ssoApiClient.put<AddTablesResponse>(url, formData);
	return response.data;
};

const deleteTable = async (locator: string, authToken: string) => {
	let url = `/tables/${locator}`;
	const response = await loadAssetsClient.delete<AddTablesResponse>(url, {
		headers: {
			Authorization: authToken,
		},
	});
	return response.data;
};

const deleteTableSSO = async (locator: string, tenantHostname: string) => {
	let url = `/tables/${locator}?ns_tenant_host=${tenantHostname}`;
	const response = await ssoApiClient.delete<AddTablesResponse>(url);
	return response.data;
};

// Queries
const useTables = (
	queryKey: string[],
	request: TablesRequest,
	token: string,
	tenantHostname?: string,
) => {
	return useQuery({
		queryKey,
		queryFn: async () => {
			return tenantHostname
				? await getTablesSSO(request, tenantHostname)
				: await getTables(request, token);
		},
	});
};

// Mutations
const useAddTableMutation = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	const client = useQueryClient();
	return useMutation(
		(newTable: AddTableRequest) => {
			return tenantHostname
				? addTableSSO(newTable, tenantHostname)
				: addTable(newTable, token);
		},
		{
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.error) {
					errorToast('Error uploading table: ' + err.response.data.error);
				} else {
					errorToast('Error uploading table');
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const useDeleteTableMutation = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	const client = useQueryClient();
	return useMutation(
		(locator: string) => {
			return tenantHostname
				? deleteTableSSO(locator, tenantHostname)
				: deleteTable(locator, token);
		},
		{
			onSuccess: () => {
				successToast('Table deleted');
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast('Error deleting table: ' + err.response.data.message);
				} else {
					errorToast('Error deleting table');
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const TablesAPI = {
	getTables,
	addTable,
	useTables,
	useAddTableMutation,
	useDeleteTableMutation,
};

export default TablesAPI;
